import { useEffect, useState } from 'react'
import { useReducedMotion } from './useReducedMotion'

/** `'open'` while present, `'closing'` while the exit animation plays. */
export type PresenceStatus = 'open' | 'closing'

export interface Presence {
  /** Render the node while true (stays true through the exit animation). */
  readonly mounted: boolean
  /** Drive the enter vs exit animation class / `data-state` off this. */
  readonly status: PresenceStatus
}

/**
 * Keep a node mounted across its exit animation — a tiny, dependency-free
 * stand-in for AnimatePresence.
 *
 * When `open` is true the node mounts immediately (`status: 'open'`, play
 * the enter animation). When `open` flips false the node stays mounted with
 * `status: 'closing'` for `durationMs` (play the exit animation), then
 * unmounts. Under `prefers-reduced-motion` the delay collapses to 0 so the
 * node unmounts at once with no held frames.
 *
 * Usage:
 *   const { mounted, status } = usePresence(open, 200)
 *   return mounted && (
 *     <div className={status === 'closing' ? 'animate-panel-out' : 'animate-panel-in'} />
 *   )
 */
export function usePresence(open: boolean, durationMs: number): Presence {
  const reduced = useReducedMotion()
  const [mounted, setMounted] = useState(open)
  const [closing, setClosing] = useState(false)

  useEffect(() => {
    if (open) {
      setMounted(true)
      setClosing(false)
      return
    }
    if (!mounted) return
    setClosing(true)
    const id = window.setTimeout(() => {
      setMounted(false)
      setClosing(false)
    }, reduced ? 0 : durationMs)
    return () => window.clearTimeout(id)
  }, [open, durationMs, reduced, mounted])

  return { mounted, status: closing ? 'closing' : 'open' }
}
